import React, { useState } from 'react';
import useTranslation from '@src/hooks/useTranslation';
import styled from 'styled-components';
import { snakeCase } from 'lodash';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { calcRatingScore } from '@src/utils/course';
import {
	SORT_OPTION_TIME_DES, SORT_OPTION_TIME_ASC, SORT_OPTION_RATING_DES, SORT_OPTION_RATING_ASC,
} from '@src/const/course';

import Modal from '@src/components/common/modal';
import Rating from '@src/components/common/rating';
import Dropdown from '@src/components/common/dropdown';
import ReviewItem from '@src/components/course/review/reviewItem';

const SORT_OPTIONS = [SORT_OPTION_TIME_DES, SORT_OPTION_TIME_ASC, SORT_OPTION_RATING_DES, SORT_OPTION_RATING_ASC];

const ratingStyle = {
	fontSize: 28,
	height: 34,
};

const Toolbar = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 20px;

	@media ${props => props.theme.device.smTablet} {
		flex-direction: column;
		margin-bottom: 16px;
	}

	@media ${props => props.theme.device.mobile} {
		flex-direction: column;
		margin-bottom: 16px;
	}
`;

const RatingAverage = styled.div`
	display: flex;
	align-items: center;

	@media ${props => props.theme.device.smTablet} {
		justify-content: space-between;
		margin-bottom: 20px;
	}

	@media ${props => props.theme.device.mobile} {
		justify-content: space-between;
		margin-bottom: 20px;
	}
`;

const ReviewNumber = styled.div`
	margin-left: 8px;
	font-size: 20px;
	color: #101010;
`;

const ReviewItemWrapper = styled(ReviewItem)`
	&:not(:last-child) {
		margin-bottom: 16px;
	}

	@media ${props => props.theme.device.smTablet} {
		&:not(:last-child) {
			margin-bottom: 8px;
		}
	}

	@media ${props => props.theme.device.mobile} {
		&:not(:last-child) {
			margin-bottom: 8px;
		}
	}
`;

const sortReviews = (reviews, sortType) => {
	switch (sortType) {
	case SORT_OPTION_TIME_DES:
		return reviews.slice().sort((a, b) => b.updatedTime - a.updatedTime);
	case SORT_OPTION_TIME_ASC:
		return reviews.slice().sort((a, b) => a.updatedTime - b.updatedTime);
	case SORT_OPTION_RATING_ASC:
		return reviews.slice().sort((a, b) => a.rating - b.rating);
	case SORT_OPTION_RATING_DES:
	default:
		return reviews.slice().sort((a, b) => b.rating - a.rating);
	}
};

interface IReview {
	id: string;
	avatar?: string;
	userName: string;
	updatedTime: string;
	rating: number;
	message: string;
	source?: string;
	originalClassName?: string;
}
interface IAddReviewModal {
	reviewArr: IReview[];
	isModalActive: boolean;
	handleClose: () => void;
}

const AllReviewModal = ({ reviewArr, isModalActive, handleClose }: IAddReviewModal): JSX.Element => {
	const { t } = useTranslation();
	const [sortType, setSortType] = useState(SORT_OPTION_RATING_DES);
	const { currentCourse: { rate } } = useCurrentCourse();
	const { score } = calcRatingScore({ totalScore: rate.total_score, totalVotes: rate.total_votes });
	const dropdownOptions = SORT_OPTIONS.map(option => ({
		type: option,
		text: t(`course:review_sort:${snakeCase(option)}`),
		isSelected: sortType === option,
	}));

	const handleSelectSort = type => { setSortType(type); };

	return (
		<Modal
			isActive={isModalActive}
			headerText={t('course:reviews')}
			handleClose={handleClose}
		>
			<Toolbar>
				<RatingAverage>
					<Rating allowHalf value={rate.total_votes ? score : 5} style={ratingStyle} />
					<ReviewNumber>{t('course:review_number', { number: reviewArr?.length })}</ReviewNumber>
				</RatingAverage>
				<Dropdown
					selectedOption={t(`course:review_sort:${snakeCase(sortType)}`) as string}
					options={dropdownOptions}
					handleSelectOption={handleSelectSort}
				/>
			</Toolbar>
			{sortReviews(reviewArr, sortType).map((review: IReview): JSX.Element => (
				<ReviewItemWrapper
					key={review.id}
					avatar={review.avatar}
					userName={review.userName}
					updatedTime={review.updatedTime}
					rating={review.rating}
					message={review.message}
					source={review.source}
					originalClassName={review.originalClassName}
				/>
			))}
		</Modal>
	);
};

export default AllReviewModal;
